import React from 'react'
import { Info } from 'lucide-react'
import { Label } from '../label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select'
import { BaseFormFieldProps, Option } from '@/types/form'
import { cn } from '@/utilities/ui'

/** Radix SelectItem cannot use an empty string value; map this to `onChange('')`. */
export const FORM_SELECT_EMPTY_VALUE = '__form_select_empty__'

const SELECT_ITEM_CLASS = cn(
  'text-sm text-gray-700 hover:bg-gray-100',
  'focus:bg-gray-100 focus:text-gray-900 cursor-pointer transition-colors duration-150',
)

export interface BaseFormSelectProps extends BaseFormFieldProps {
  options: Option[]
  selectClassName?: string
  testId?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  helperText?: string
  errorText?: string
  disabled?: boolean
  ariaLabeledBy?: string
  ariaDescribedBy?: string
  variant?: 'outlined' | 'filled' | 'standard'
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  onOpenChange?: (open: boolean) => void
  onFocus?: () => void
  onBlur?: (value: string) => void
  isGroup?: boolean
  groupError?: string
  showError?: boolean
  disableFocusRing?: boolean
  trailingAction?: React.ReactNode
  /** When set, the menu includes an item that clears the value (optional fields). */
  emptyOptionLabel?: string
}

export function BaseFormSelect({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  showInfoIcon = false,
  error,
  className = '',
  selectClassName = '',
  labelClassName = '',
  testId,
  startIcon,
  endIcon,
  helperText,
  errorText,
  disabled = false,
  ariaLabeledBy,
  ariaDescribedBy,
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
  onOpenChange,
  onFocus,
  onBlur,
  // Group handling
  isGroup = false,
  groupError,
  // Legacy props for backward compatibility
  showError = true,
  disableFocusRing = false,
  trailingAction,
  emptyOptionLabel,
}: Readonly<BaseFormSelectProps>) {
  const latestValueRef = React.useRef(value)

  React.useEffect(() => {
    latestValueRef.current = value
  }, [value])

  const handleValueChange = (newValue: string) => {
    const nextValue = emptyOptionLabel && newValue === FORM_SELECT_EMPTY_VALUE ? '' : newValue
    latestValueRef.current = nextValue
    onChange(nextValue)
  }

  // Keep the select controlled when clearing. `value={undefined}` makes Radix
  // uncontrolled and it snaps back to the previous state on the first clear.
  const selectValue = emptyOptionLabel ? value || FORM_SELECT_EMPTY_VALUE : value

  // For groups: never show errors (they should be shown outside the group)
  // For individual selects: show error normally
  const shouldShowError = isGroup ? false : showError

  const hasError = Boolean(error || errorText || groupError)

  // Base classes
  const baseSelectClasses = [
    'w-full px-3 py-2 border rounded-lg transition-colors duration-200',
    disableFocusRing
      ? 'outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0'
      : 'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    '[&>span]:data-[placeholder]:text-tenant-description-text',
  ]

  // Variant classes
  const variantClasses = {
    outlined: disableFocusRing
      ? 'border-gray-300'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
    filled: disableFocusRing
      ? 'border-transparent bg-gray-100 focus:bg-white'
      : 'border-transparent bg-gray-100 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
    standard: disableFocusRing
      ? 'border-b-2 border-gray-300 rounded-none'
      : 'border-b-2 border-gray-300 rounded-none focus:border-blue-500',
  }

  // Size classes
  const sizeClasses = {
    small: 'text-sm py-1.5',
    medium: 'text-base py-2',
    large: 'text-lg py-3',
  }

  // Error classes
  const errorClasses = hasError ? 'border-error focus:border-error focus:ring-error' : ''

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : ''

  // Icon padding classes
  const iconPaddingClasses = [startIcon ? 'pl-10' : '', endIcon ? 'pr-10' : '']
    .filter(Boolean)
    .join(' ')

  const selectClasses = [
    ...baseSelectClasses,
    variantClasses[variant],
    sizeClasses[size],
    errorClasses,
    widthClasses,
    iconPaddingClasses,
    selectClassName,
  ].join(' ')

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
      {label && (
        <Label
          htmlFor={id}
          className={`block text-sm text-description-text mb-1 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-required ml-1">*</span>}
          {showInfoIcon && <Info className="inline-block w-4 h-4 ml-2 text-gray-400" />}
        </Label>
      )}

      <div className="relative">
        {startIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
            {startIcon}
          </div>
        )}

        <Select
          value={selectValue}
          onValueChange={handleValueChange}
          disabled={disabled}
          onOpenChange={(open) => {
            // Call onBlur when the select closes
            if (!open && onBlur) {
              onBlur(latestValueRef.current)
            }
            if (onOpenChange) {
              onOpenChange(open)
            }
          }}
        >
          <SelectTrigger
            id={id}
            data-testid={testId}
            className={selectClasses}
            onFocus={onFocus}
            aria-labelledby={ariaLabeledBy}
            aria-describedby={ariaDescribedBy}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent
            className="bg-white border border-gray-200 rounded-md shadow-lg max-h-80"
            id={`${id}-dropdown`}
          >
            {emptyOptionLabel ? (
              <SelectItem value={FORM_SELECT_EMPTY_VALUE} className={SELECT_ITEM_CLASS}>
                {emptyOptionLabel}
              </SelectItem>
            ) : null}
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} className={SELECT_ITEM_CLASS}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {endIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
            {endIcon}
          </div>
        )}
        {trailingAction}
      </div>

      {/* Helper text or error message — match FormInput: no reserved height when nothing to show */}
      {!isGroup && (
        <div
          className={
            helperText || (shouldShowError && (error || errorText || groupError)) ? 'mt-0.5' : 'h-0'
          }
        >
          {helperText || (shouldShowError && (error || errorText || groupError)) ? (
            <p
              className={`text-xs leading-4 ${hasError ? 'text-red-600' : 'text-gray-500'}`}
              role={hasError ? 'alert' : undefined}
            >
              {error || errorText || groupError || helperText}
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}

export function FormSelect(props: Readonly<BaseFormSelectProps>) {
  return <BaseFormSelect {...props} />
}
